
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { useMemo, useState } from 'react';
import { DateTimeField } from '@mui/x-date-pickers/DateTimeField';

import dayjs from 'dayjs'
import utc from "dayjs/plugin/utc";
dayjs.extend(utc);

const dateTimeInputPattern = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/;

const normalizeDateTimeInput = (inputValue: string) => inputValue.replace(/\u200e/g, '').trim();

const isBlankDateTimeInput = (inputValue: string) => !/\d/.test(normalizeDateTimeInput(inputValue));

const isCompleteDateTimeInput = (inputValue: string) => dateTimeInputPattern.test(normalizeDateTimeInput(inputValue));

interface DatetimeInputProps {
    onChange: (value: number | null) => void; // epoch in seconds
    value: number | null; // epoch in seconds
    disabled?: boolean
    minDateTime?: number | null
    maxDateTime?: number | null
    minYear?: number
    maxYear?: number
    onOutOfRange?: () => void
    showUtcSuffix?: boolean
    compact?: boolean
    className?: string
    onValidationChange?: (isValid: boolean, errorMessage?: string, hasInvalidInput?: boolean) => void; // New prop to track validity
}

/**
 * A datetime input component that allows users to select a datetime
 * in UTC timezone.
 *
 * @param {{ onChange: (value: number | null) => void, value: number | null, disabled?: boolean }}
 * @returns {JSX.Element}
 */
const DatetimeInput: React.FC<DatetimeInputProps> = ({
    onChange,
    value,
    disabled = false,
    minDateTime,
    maxDateTime,
    minYear,
    maxYear,
    onOutOfRange,
    showUtcSuffix = true,
    compact = false,
    className = '',
    onValidationChange,
}: DatetimeInputProps): JSX.Element => {
    const timeValue = useMemo(() => {
        if (value === null || value === undefined || value < 0) return null;
        return dayjs(value * 1000).utc();
    }, [value]);

    const minDateTimeValue = useMemo(() => {
        if (minDateTime === null || minDateTime === undefined || minDateTime < 0) return undefined;
        return dayjs(minDateTime * 1000).utc();
    }, [minDateTime]);

    const maxDateTimeValue = useMemo(() => {
        if (maxDateTime === null || maxDateTime === undefined || maxDateTime < 0) return undefined;
        return dayjs(maxDateTime * 1000).utc();
    }, [maxDateTime]);

    const onRawInputChange = (event: React.FormEvent<HTMLInputElement>) => {
        const inputValue = event.currentTarget.value;
        if (isBlankDateTimeInput(inputValue)) {
            onChange(null);
        }
    };

    const onRawInputBlur = (event: React.FocusEvent<HTMLInputElement>) => {
        const inputValue = event.currentTarget.value;
        if (isBlankDateTimeInput(inputValue)) {
            onChange(null);
        }
    };

    const onInputChange = (value: any) => {
        if (value === null) {
            onValidationChange?.(false, 'Please enter a valid date and time', true);
            return;
        }
        if (!value || !dayjs(value).isValid()) {
            onValidationChange?.(false, 'Invalid date and time format');
            return;
        }
        const d = dayjs.utc(value);

        if (d.unix() < 0) {
            onValidationChange?.(false, 'Invalid date');
            return;
        }
        const year = d.year();
        if ((minYear && year < minYear) || (maxYear && year > maxYear)) {
            onValidationChange?.(false, `Year must be between ${minYear} and ${maxYear}`);
            onOutOfRange?.();
            return;
        }

        // Valid datetime - clear error and notify parent
        onValidationChange?.(true, undefined, false);
        onChange(d.unix());
    };

    const onDateTimeFieldError = (error: any) => {
        if (error) {
            // MUI DateTimeField error - field has invalid input
            const errorMessage = typeof error === 'string'
                ? error
                : 'Please enter a valid date and time';
            onValidationChange?.(false, errorMessage);
        }
    };

    return (
        <div className={`d-flex align-items-center overflow-hidden w-100 p-0 ${disabled ? 'opacity-75 pointer-events-none cursor-auto' : ''} ${className}`}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DateTimeField
                    disabled={disabled}
                    sx={{
                        borderRadius: compact ? (showUtcSuffix ? '4px' : '4px 0 0 4px') : '0px',
                        background: compact ? 'rgba(9, 9, 9, 0.65)!important' : 'rgba(10, 10, 10, 0.65)!important',
                        '.MuiFormControl-root': {
                            paddingRight: '0px',
                        },
                        '.MuiInputBase-input': {
                            fontSize: compact ? '12px !important' : '14px !important',
                            color: compact ? '#F0F0F0 !important' : undefined,
                        },
                        '.MuiOutlinedInput-input': {
                            fontSize: compact ? '12px !important' : '14px !important',
                            color: compact ? '#F0F0F0 !important' : undefined,
                        },
                        '.MuiOutlinedInput-root': {
                            height: compact ? '24px !important' : '30px !important',
                            paddingRight: '0px',
                            borderRadius: compact ? (showUtcSuffix ? '4px' : '4px 0 0 4px') : '0px',
                        },
                        '.MuiInputAdornment-root': {
                            display: 'none',
                        },
                        '.MuiOutlinedInput-notchedOutline': {
                            borderRadius: compact ? (showUtcSuffix ? '4px' : '4px 0 0 4px') : '0px',
                            border: compact ? '0.5px solid rgba(240,240,240,0.30) !important' : 'none',
                            borderRight: showUtcSuffix
                              ? '0.5px solid rgba(240,240,240,0.30) !important'
                              : (compact ? 'none' : 'none'),
                        },
                        '.Mui-disabled': {
                            color: 'rgba(240,240,240,0.30) !important',
                            '-webkit-text-fill-color ': 'rgba(240,240,240,0.30) !important',
                            cursor: 'auto'
                        }
                    }}
                    className={compact ? 'w-100' : 'rounded-3 w-90'}
                    ampm={false} format='YYYY-MM-DD HH:mm:ss'
                    minDateTime={minDateTimeValue}
                    maxDateTime={maxDateTimeValue}
                    slotProps={{
                        textField: {
                            inputProps: {
                                onInput: onRawInputChange,
                                onBlur: onRawInputBlur,
                            },
                        },
                    }}
                    value={timeValue}
                    onChange={onInputChange}
                    onError={onDateTimeFieldError}
                />
            </LocalizationProvider>
            {showUtcSuffix && <span className='opacity-75 fs-12 px-2'>UTC</span>}
        </div>
    )
}

export default DatetimeInput
